import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ListMusic, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  fetchSetlists,
  fetchSongs,
  createSetlist,
  deleteSetlist,
} from "@/lib/api";
import { formatTotal } from "@/lib/format";

const inputCls =
  "bg-[#121212] border-[#2D2D2D] text-white placeholder:text-zinc-600 focus-visible:ring-[#00F0FF] focus-visible:ring-2";

export default function Setlists() {
  const [setlists, setSetlists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [venue, setVenue] = useState("");
  const [showDate, setShowDate] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    fetchSetlists().then(setSetlists);
    fetchSongs().then(setSongs);
  };

  useEffect(() => {
    load();
  }, []);

  const songMap = Object.fromEntries(songs.map((s) => [s.id, s]));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Informe um nome para o setlist");
      return;
    }
    try {
      await createSetlist({
        name: name.trim(),
        venue: venue.trim(),
        show_date: showDate.trim(),
      });
      toast.success("Setlist criado");
      setName("");
      setVenue("");
      setShowDate("");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Erro ao criar setlist");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSetlist(deleteId);
      toast.success("Setlist excluído");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-2">
            // gerenciador de shows
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Setlists
          </h1>
          <p className="text-zinc-500 mt-2 text-sm md:text-base">
            Monte seus shows, reordene faixas e veja o tempo total estimado.
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold h-11"
              data-testid="create-setlist-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Novo setlist
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1A1A1A] border-[#2D2D2D] text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                Novo setlist
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-zinc-300">Nome *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex.: Show de Sábado – Pub X"
                  className={inputCls}
                  data-testid="setlist-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue" className="text-zinc-300">Local</Label>
                <Input
                  id="venue"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="Ex.: Bar do Zé – SP"
                  className={inputCls}
                  data-testid="setlist-venue-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date" className="text-zinc-300">Data</Label>
                <Input
                  id="date"
                  value={showDate}
                  onChange={(e) => setShowDate(e.target.value)}
                  placeholder="Ex.: 22/03/2026"
                  className={inputCls}
                  data-testid="setlist-date-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold"
                  data-testid="setlist-create-submit"
                >
                  Criar setlist
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {setlists.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#2D2D2D] bg-[#1A1A1A]/60 p-16 text-center">
          <ListMusic className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <p className="text-lg font-semibold mb-1">Nenhum setlist criado</p>
          <p className="text-zinc-500 text-sm">
            Crie seu primeiro setlist clicando em &ldquo;Novo setlist&rdquo;.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {setlists.map((s) => {
            const totalSec = s.song_ids
              .map((id) => songMap[id])
              .filter(Boolean)
              .reduce((acc, song) => acc + (song.duration_seconds || 0), 0);
            return (
              <div
                key={s.id}
                data-testid={`setlist-item-${s.id}`}
                className="group p-5 rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] hover:border-[#00F0FF]/60 transition-all duration-150"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <Link
                    to={`/setlists/${s.id}`}
                    className="flex-1 min-w-0"
                    data-testid={`open-setlist-${s.id}`}
                  >
                    <h3 className="font-semibold text-lg leading-tight truncate group-hover:text-[#00F0FF] transition-colors">
                      {s.name}
                    </h3>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteId(s.id)}
                    data-testid={`delete-setlist-${s.id}`}
                    className="text-zinc-500 hover:text-red-400 hover:bg-[#2D2D2D] h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="text-sm text-zinc-500 mb-4 space-y-0.5">
                  {s.venue && <p>{s.venue}</p>}
                  {s.show_date && <p className="font-mono text-xs">{s.show_date}</p>}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-[#2D2D2D]">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600 font-mono">
                      Tempo total
                    </span>
                    <span className="font-mono text-[#00F0FF] text-sm">
                      {formatTotal(totalSec)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-500">
                      {s.song_ids.length} faixas
                    </span>
                    <Link to={`/setlists/${s.id}`}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D] hover:text-[#00F0FF] h-8"
                      >
                        Abrir <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1A1A1A] border-[#2D2D2D] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir setlist?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              O setlist será removido. As músicas do banco permanecem intactas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-setlist"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
